import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, X, Send } from 'lucide-react';
import { playNotifySound, initAudio } from './notifySound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveChat = () => {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('chat_session') || 'null');
    } catch { return null; }
  });
  const [form, setForm] = useState({ name: '', surname: '', phone: '' });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [ended, setEnded] = useState(false);
  const [unread, setUnread] = useState(0);
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevAdminMsgCount = useRef(0);

  // Ses için audio context'i unlock et (kullanıcı etkileşiminden sonra)
  useEffect(() => { initAudio(); }, []);

  // Anasayfadaki "Canlı Destek" butonu event'ini dinle
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('openLiveChat', handler);
    return () => window.removeEventListener('openLiveChat', handler);
  }, []);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (open) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [messages, open]);

  // Focus input when opened
  useEffect(() => {
    if (open && session && !ended) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, session, ended]);

  // Reset unread when opened
  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  // Poll for messages every 3s
  useEffect(() => {
    if (!session?.session_id) return;
    let cancelled = false;
    const fetchMessages = async () => {
      try {
        const res = await axios.get(`${API}/chat/messages/${session.session_id}`);
        if (cancelled) return;
        const newMsgs = res.data.messages || [];
        setMessages(prev => {
          // Count new admin messages when chat is closed
          if (!open) {
            const prevAdminCount = prev.filter(m => m.from === 'admin').length;
            const newAdminCount = newMsgs.filter(m => m.from === 'admin').length;
            if (newAdminCount > prevAdminCount) {
              setUnread(u => u + (newAdminCount - prevAdminCount));
            }
          }
          return newMsgs;
        });
        // Yeni admin mesajı geldiyse ses çal (chat açık veya kapalı fark etmez)
        const adminCount = newMsgs.filter(m => m.from === 'admin').length;
        if (prevAdminMsgCount.current > 0 && adminCount > prevAdminMsgCount.current) {
          playNotifySound('customer');
        }
        prevAdminMsgCount.current = adminCount;
      } catch (err) {
        if (err?.response?.status === 410) {
          // Admin deleted this chat
          setEnded(true);
          setMessages([]);
          localStorage.removeItem('chat_session');
        }
      }
    };
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [session, open]);

  // Heartbeat every 20s
  useEffect(() => {
    if (!session?.session_id) return;
    const beat = () => axios.post(`${API}/chat/heartbeat/${session.session_id}`).catch(() => {});
    beat();
    const interval = setInterval(beat, 20000);
    return () => clearInterval(interval);
  }, [session]);

  const startChat = async (e) => {
    e.preventDefault();
    if (!form.name || !form.surname || !form.phone) return;
    setStarting(true);
    try {
      const productMatch = location.pathname.match(/^\/urun\/(.+)$/);
      const productId = productMatch ? productMatch[1] : '';
      const res = await axios.post(`${API}/chat/register`, {
        name: form.name.trim(),
        surname: form.surname.trim(),
        phone: form.phone.trim(),
        page: location.pathname,
        product_id: productId,
      });
      const sess = { session_id: res.data.session_id, ...form };
      localStorage.setItem('chat_session', JSON.stringify(sess));
      setSession(sess);
      setEnded(false);
    } catch (err) {
      alert('Sohbet başlatılamadı: ' + (err.response?.data?.detail || err.message));
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const txt = input.trim();
    setInput('');
    setSending(true);
    // Optimistic add
    setMessages(prev => [...prev, {
      from: 'customer', text: txt,
      created_at: new Date().toISOString(),
      _optimistic: true,
    }]);
    try {
      await axios.post(`${API}/chat/send`, { session_id: session.session_id, text: txt });
    } catch (err) {
      alert('Mesaj gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const restartChat = () => {
    localStorage.removeItem('chat_session');
    setSession(null);
    setEnded(false);
    setMessages([]);
    setForm({ name: '', surname: '', phone: '' });
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="live-chat-open-button"
          className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 group"
          aria-label="Canlı Destek"
        >
          <div className="relative">
            <div className="w-14 h-14 bg-[#1a1a1a] hover:bg-[#333333] rounded-full flex items-center justify-center shadow-xl transition-all duration-300 group-hover:scale-105">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            {unread > 0 && (
              <span data-testid="chat-unread-badge" className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                {unread}
              </span>
            )}
            <div className="absolute right-16 top-1/2 -translate-y-1/2 bg-[#1a1a1a] text-white text-xs px-3 py-1.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              💬 Canlı Destek
            </div>
          </div>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="live-chat-panel"
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-sm h-[500px] max-h-[75vh] md:h-[550px] md:max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 animate-slide-in"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#333333] text-white px-4 py-3 flex items-center justify-between shadow-md">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-sm">Hasankeyf Tesettür</div>
                <div className="text-[11px] text-white/70 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  Canlı Destek
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="live-chat-close-button"
              className="p-1.5 hover:bg-white/10 rounded-full transition"
              aria-label="Kapat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          {ended ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <MessageCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-serif font-semibold text-lg text-[#2c1810] mb-2">Sohbet Sonlandı</h3>
              <p className="text-sm text-gray-600 mb-4">Bu sohbet temsilci tarafından sonlandırıldı. Yeni bir sohbet başlatabilirsiniz.</p>
              <button
                onClick={restartChat}
                data-testid="restart-chat-button"
                className="bg-[#1a1a1a] hover:bg-[#333333] text-white px-5 py-2 rounded-lg text-sm font-medium transition"
              >
                Yeni Sohbet Başlat
              </button>
            </div>
          ) : !session ? (
            /* Registration Form */
            <form onSubmit={startChat} className="flex-1 flex flex-col p-5 gap-3 overflow-y-auto">
              <div className="text-center mb-2">
                <div className="text-4xl mb-2">🌸</div>
                <h3 className="font-serif font-semibold text-lg text-[#2c1810]">Merhaba!</h3>
                <p className="text-xs text-gray-600 mt-1">Sohbete başlamak için bilgilerinizi girin:</p>
              </div>
              <input
                type="text"
                required
                autoFocus
                placeholder="Adınız *"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                data-testid="chat-form-name"
                className="w-full border border-gray-200 px-4 py-2.5 rounded-lg text-sm focus:border-[#1a1a1a] focus:outline-none transition"
              />
              <input
                type="text"
                required
                placeholder="Soyadınız *"
                value={form.surname}
                onChange={e => setForm({ ...form, surname: e.target.value })}
                data-testid="chat-form-surname"
                className="w-full border border-gray-200 px-4 py-2.5 rounded-lg text-sm focus:border-[#1a1a1a] focus:outline-none transition"
              />
              <input
                type="tel"
                required
                placeholder="Telefon (05XX XXX XX XX) *"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                data-testid="chat-form-phone"
                className="w-full border border-gray-200 px-4 py-2.5 rounded-lg text-sm focus:border-[#1a1a1a] focus:outline-none transition"
              />
              <button
                type="submit"
                disabled={starting}
                data-testid="chat-form-submit"
                className="w-full bg-[#1a1a1a] hover:bg-[#333333] text-white py-2.5 rounded-lg text-sm font-medium tracking-wide transition disabled:opacity-50 mt-2"
              >
                {starting ? 'Başlatılıyor...' : 'SOHBETİ BAŞLAT'}
              </button>
              <p className="text-[10px] text-gray-400 text-center mt-2">
                Yanıt süresi genellikle 1-15 dakika
              </p>
            </form>
          ) : (
            /* Chat Interface */
            <>
              <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-[#faf7f2]">
                {messages.map((m, idx) => (
                  <ChatBubble key={idx} message={m} />
                ))}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={sendMessage} className="p-3 bg-white border-t flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Mesajınızı yazın..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  data-testid="chat-message-input"
                  className="flex-1 border border-gray-200 px-3 py-2 rounded-full text-sm focus:border-[#1a1a1a] focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  data-testid="chat-send-button"
                  className="w-10 h-10 bg-[#1a1a1a] hover:bg-[#333333] disabled:opacity-40 text-white rounded-full flex items-center justify-center transition"
                  aria-label="Gönder"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

// Message bubble - handles text, links, product cards
const ChatBubble = ({ message }) => {
  const isAdmin = message.from === 'admin';
  const time = message.created_at ? new Date(message.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '';

  // Parse text for links (http:// or /urun/ or /sepet)
  const renderText = (text) => {
    const parts = text.split(/(https?:\/\/\S+|\/urun\/\S+|\/sepet|\/kategori\/\S+)/g);
    return parts.map((part, i) => {
      if (/^https?:\/\//.test(part)) {
        return <a key={i} href={part} target="_blank" rel="noreferrer" className="underline font-medium">{part}</a>;
      }
      if (/^\/(urun|sepet|kategori)/.test(part)) {
        return <a key={i} href={part} className="underline font-medium">{part}</a>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[80%] ${isAdmin ? '' : 'flex flex-col items-end'}`}>
        <div className={`px-3 py-2 rounded-2xl text-sm break-words whitespace-pre-wrap ${
          isAdmin
            ? 'bg-white border border-gray-200 text-[#2c1810] rounded-tl-sm'
            : 'bg-[#1a1a1a] text-white rounded-tr-sm'
        }`}>
          {renderText(message.text)}
        </div>
        <div className={`text-[10px] text-gray-400 mt-1 ${isAdmin ? 'ml-1' : 'mr-1'}`}>
          {time}{isAdmin ? ' • Temsilci' : ''}
        </div>
      </div>
    </div>
  );
};

export default LiveChat;
